// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { Document, DocumentType, ErrorCause, Feedback, LoadingStatus } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { SelectDocumentType } from '../../fields/SelectDocumentType/SelectDocumentType';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface FormEditDocumentProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
}

export const FormEditDocument = ({ onClose, onSuccess, setStatus }: FormEditDocumentProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDocument = searchParams.get('dialogDocument');

	// state
	const [document, setDocument] = useState<Document | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		type: '',
	});

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-document';

	// fetch document
	const { isLoading: isLoadingDocument } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/documents/${paramDocument}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setDocument(res.data[0]);
			}
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		try {
			// clear feedback
			setFeedback(null);

			// set status
			setStatus('LOADING');

			if (!paramDocument) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: Record<string, unknown> = {
				type: values.type ? Number(values.type) : null,
			};

			const fetchEditDocument = await fetch(
				`${process.env.REACT_APP_API_URL}/documents/${paramDocument}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			const resEditDocument = await fetchEditDocument.json();

			if (resEditDocument.error) {
				throw new Error(resEditDocument.error.message, {
					cause: {
						id: resEditDocument.error.id,
						details: resEditDocument.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.documentUpdated', { name: document?.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'ID_NON_EXISTENT') {
					feedbackNew.alert.children = t('error.idNonExistent');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set status
			setStatus('DEFAULT');
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingDocument) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						InputProps={{
							readOnly: true,
						}}
						label={t('name')}
						name="name"
						value={document?.name || ''}
					/>
				</Grid>
				<Grid item xs={12}>
					<SelectDocumentType
						id={`${idForm}-type`}
						name="type"
						onChange={handleChangeValue}
						value={values.type}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when document loads
	useEffect(() => {
		if (document) {
			let typeNew = '';

			if (document.type) {
				const documentType = document.type as DocumentType;
				typeNew = String(documentType?.id || document.type);
			}

			setValues({
				type: typeNew,
			});
		}
	}, [document]);

	return (
		<form id={idForm} onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
